import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { useStores } from '../../hooks/use-stores';
import WebhookEventsTable from '../../components/super/webhooks/WebhookEventsTable';
import {
    WebhookStatusChip,
    eventNamesOf,
    preStyle,
    webhookScope,
} from '../../components/super/webhooks/webhookFormatters';

// One labelled row of the detail panel.
function Field({ label, children }) {
    return (
        <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary">
                {label}
            </Typography>
            <Typography variant="body1" style={{ wordBreak: 'break-all' }}>
                {children ?? '-'}
            </Typography>
        </Grid>
    );
}

function WebhookView() {
    const { webhookId } = useParams();
    const { webhooksStore } = useStores();
    const { webhook, loadingWebhook } = webhooksStore;
    const navigate = useNavigate();

    useEffect(() => {
        webhooksStore.loadWebhook(webhookId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webhookId]);

    if (loadingWebhook || !webhook) return <ThemedSpinner />;

    // The API strips the signing secret before it ever reaches the browser and
    // reports only this flag, so an operator can still tell a signed webhook
    // from an unsigned one.
    const isSigned = Boolean(webhook.hasSignatureSecret);

    return (
        <Grid container direction="row" spacing={2}>
            <Grid item xs={12}>
                <Box
                    mt={4}
                    mb={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography variant="h2">
                        Webhook #{webhook.id} {webhook.name}
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/webhooks')}
                    >
                        Back to webhooks
                    </Button>
                </Box>

                <Paper style={{ padding: 16, textAlign: 'left' }}>
                    <Grid container spacing={2}>
                        <Field label="Name">{webhook.name}</Field>
                        <Field label="Status">
                            <WebhookStatusChip status={webhook.status} />
                        </Field>
                        <Field label="Url">{webhook.url}</Field>
                        <Field label="Scope">{webhookScope(webhook)}</Field>
                        <Field label="Events">{eventNamesOf(webhook)}</Field>
                        <Field label="Signed">{isSigned ? 'Yes' : 'No'}</Field>
                        <Field label="Company id">{webhook.companyId}</Field>
                        <Field label="Partner id">{webhook.partnerId}</Field>
                        <Field label="Unique id">{webhook.unique_id}</Field>
                        <Field label="Created">
                            {webhook.created
                                ? moment(webhook.created).format('LLL')
                                : '-'}
                        </Field>
                        <Field label="Updated">
                            {webhook.updated
                                ? moment(webhook.updated).format('LLL')
                                : '-'}
                        </Field>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">
                                Definition
                            </Typography>
                            {/* The secret is already stripped server-side; the
                                replacer is a second line of defence, since
                                definition is free-form JSON that could grow
                                another sensitive key later. */}
                            <pre style={preStyle}>
                                {JSON.stringify(
                                    webhook.definition,
                                    (key, value) =>
                                        key === 'signatureSecret'
                                            ? '[REDACTED]'
                                            : value,
                                    2
                                )}
                            </pre>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <Box mt={4} mb={2}>
                    <Typography variant="h3" align="left">
                        Deliveries
                    </Typography>
                </Box>
                {/* Same table as the global events page, scoped to this webhook. */}
                <WebhookEventsTable
                    fixedWebhookId={Number(webhookId)}
                    rowsPerPage={10}
                />
            </Grid>
        </Grid>
    );
}

export default observer(WebhookView);
